"use client";

import { useSetAtom } from "jotai";
import { useParams } from "next/navigation";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import LocaleLink from "@/lib/i18n/locale-link";
import { currentLanguageAtom } from "@/store/languageAtom";
import { useClientAreaLanguages } from "./hooks/use-client-area-languages";
import { SUPPORTED_LANGUAGES } from "./settings";
import { buildLocaleString } from "./utils";
export function LanguageSwitcherInFooter() {
  const {
    t
  } = useTranslation();
  const params = useParams();
  const [open, setOpen] = useState(false);
  const setCurrentLanguage = useSetAtom(currentLanguageAtom);
  const currentLocale = params.locale as string;

  // 获取动态语言列表和当前状态
  const {
    languages,
    isLoading,
    currentArea,
    displayLanguage
  } = useClientAreaLanguages();

  // 如果语言列表仅有一个，则不展示切换控件
  const shouldHideSwitcher = !isLoading && Array.isArray(languages) && languages.length <= 1;
  if (shouldHideSwitcher) {
    return null;
  }

  // 语言名称映射（统一使用小写键值）
  const languageNames: Record<string, string> = {
    "en-us": "English",
    "zh-hant": "中文(繁體)",
    "zh-hans": "中文(简体)",
    "ja-jp": "日本語",
    "ko-kr": "한국어",
    "de-de": "Deutsch",
    "fr-fr": "Français",
    "it-it": "Italiano",
    "es-es": "Español",
    "ru-ru": "Русский",
    "hi-in": "हिन्दी",
    "pt-br": "Português",
    "th-th": "ไทย",
    "vi-vn": "Tiếng Việt",
    "id-id": "Bahasa Indonesia",
    "ms-my": "Bahasa Melayu"
  };

  // 获取当前显示的语言名称
  const getCurrentLanguageName = () => {
    // 使用displayLanguage，始终显示URL中的语言，即使实际i18n使用兜底语言
    const normalizedLanguage = displayLanguage.toLowerCase();
    const displayName = languageNames[normalizedLanguage] || normalizedLanguage;
    return displayName;
  };

  // 获取语言显示名称的统一函数
  const getLanguageDisplayName = (languageCode: string) => {
    const normalizedLanguage_0 = languageCode.toLowerCase();
    const displayName_0 = languageNames[normalizedLanguage_0] || languageNames[languageCode] || languageCode;
    return displayName_0;
  };

  // 检查是否是当前选中的语言
  const isCurrentLanguage = (languageCode_0: string) => {
    // 使用displayLanguage进行比较，确保高亮显示URL中的语言
    const isCurrent = languageCode_0.toLowerCase() === displayLanguage.toLowerCase();
    return isCurrent;
  };

  // 处理语言切换的函数
  const handleLanguageChange = (targetLanguage: string) => {
    // 如果是当前语言，不执行切换
    if (isCurrentLanguage(targetLanguage)) {
      setOpen(false);
      return;
    }

    // 双重校验：检查目标语言是否既在可用语言列表中，也在 i18n 配置中
    const normalizedTargetLanguage = targetLanguage.toLowerCase();
    const isInValidLanguages = languages.includes(normalizedTargetLanguage);
    const isInSupportedLanguages = SUPPORTED_LANGUAGES.includes(normalizedTargetLanguage as any);

    // 如果目标语言不可用或不在 i18n 配置中，使用英语兜底
    const finalLanguage = isInValidLanguages && isInSupportedLanguages ? normalizedTargetLanguage : "en-us";

    // 更新 atom 中的当前语言，确保使用标准格式
    setCurrentLanguage(finalLanguage);

    // 同步更新 i18n 实例的语言
    if (typeof window !== "undefined" && (window as any).i18next) {
      ;
      (window as any).i18next.changeLanguage(finalLanguage);
    }
    const currentPathname = window.location.pathname;
    const queryString = window.location.search;
    if (!currentPathname) {
      console.error("无法从 window.location.pathname 获取当前路径");
      setOpen(false);
      return;
    }

    // 构建新的locale字符串，使用原始语言代码构建URL，但内部使用兜底语言
    const normalizedArea = (currentArea || "").toLowerCase();
    const newLocale = buildLocaleString(targetLanguage,
    // 使用原始目标语言构建URL
    normalizedArea, languages);
    const newPath = currentPathname.replace(currentLocale, newLocale);
    window.location.href = newPath + queryString;
    setOpen(false);
  };
  return <Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<div className="border-footer-gray mt-4 flex cursor-pointer items-center justify-center border px-2 py-2.5">
					<span className="text-xs">{t("languageSwitcher.label")}</span>
					<span className="border-l-footer-gray mx-1 h-3.5 border-l"></span>
					<span className="text-xs font-bold text-white">
						{getCurrentLanguageName()}
					</span>
				</div>
			</DialogTrigger>
			<DialogContent className="w-[740px] p-12 md:max-w-[410px] md:p-8">
				<DialogHeader>
					<DialogTitle className="text-center text-xl leading-none">
						{t("languageSwitcher.title")}
					</DialogTitle>
				</DialogHeader>
				<div className="flex flex-col">
					{isLoading ? <div className="py-3 text-center text-lg leading-none font-bold text-gray-400">
							{t("languageSwitcher.loading", "Loading...")}
						</div> : languages.map(lang => {
          const isActive = isCurrentLanguage(lang);
          return <LocaleLink key={lang} href="#" onClick={e => {
            e.preventDefault();
            handleLanguageChange(lang);
          }} className={`py-3 text-center text-lg leading-none font-bold ${isActive ? "text-destructive cursor-default" : "hover:text-destructive cursor-pointer"}`}>
									{getLanguageDisplayName(lang)}
								</LocaleLink>;
        })}
				</div>
			</DialogContent>
		</Dialog>;
}